import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { PoClosureSearchResult } from "models/po-closure";
import { usePoClosureSearch } from "src/hooks/po-closure/usePoClosureSearch";
import { useCreatePoClosureRequest } from "src/hooks/po-closure/useCreatePoClosureRequest";

const MAX_REASON_LENGTH = 500;

const PoClosureForm: React.FC = () => {
  const [poNumber, setPoNumber] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [selectedPo, setSelectedPo] = useState<PoClosureSearchResult | null>(null);
  const [reason, setReason] = useState("");
  const navigate = useNavigate();

  const { items, loading: searchLoading, error: searchError, searchOpenPos } =
    usePoClosureSearch();
  const {
    loading: submitLoading,
    error: submitError,
    createPoClosureRequest,
  } = useCreatePoClosureRequest();

  const canSearch = poNumber.trim().length > 0 || vendorName.trim().length > 0;
  const trimmedReason = reason.trim();
  const reasonRemaining = MAX_REASON_LENGTH - trimmedReason.length;

  const canSubmit = useMemo(
    () =>
      selectedPo != null &&
      trimmedReason.length > 0 &&
      trimmedReason.length <= MAX_REASON_LENGTH &&
      !submitLoading,
    [selectedPo, trimmedReason, submitLoading],
  );

  const handleSearch = async () => {
    await searchOpenPos({ poNumber, vendorName });
    setSelectedPo(null);
  };

  const handleSubmit = async () => {
    if (!selectedPo || !canSubmit) {
      return;
    }
    const response = await createPoClosureRequest(selectedPo, trimmedReason);
    navigate(`/po-closures/${response.requestId}`);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Self-Serve PO Closure
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Search open NetSuite purchase orders, pick one, and submit a closure reason.
        </Typography>

        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            handleSearch();
          }}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(220px, 1fr) minmax(220px, 1fr) auto",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <TextField
            label="PO Number"
            value={poNumber}
            onChange={(event) => setPoNumber(event.target.value)}
            fullWidth
          />
          <TextField
            label="Vendor Name"
            value={vendorName}
            onChange={(event) => setVendorName(event.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            type="submit"
            disabled={!canSearch || searchLoading}
            startIcon={!searchLoading ? <SearchIcon /> : undefined}
            sx={{
              minWidth: { xs: "100%", md: 160 },
              height: 56,
              whiteSpace: "nowrap",
            }}
          >
            {searchLoading ? <CircularProgress size={22} /> : "Search POs"}
          </Button>
        </Box>

        {searchError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {searchError}
          </Typography>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>PO Number</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Remaining</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No open POs found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow
                    key={item.internalId}
                    hover
                    selected={selectedPo?.internalId === item.internalId}
                    onClick={() => setSelectedPo(item)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <input
                        type="radio"
                        checked={selectedPo?.internalId === item.internalId}
                        onChange={() => setSelectedPo(item)}
                      />
                    </TableCell>
                    <TableCell>{item.tranId}</TableCell>
                    <TableCell>{item.vendorName}</TableCell>
                    <TableCell>{item.status || "-"}</TableCell>
                    <TableCell>{item.amountTotal ?? "-"}</TableCell>
                    <TableCell>{item.amountRemaining ?? "-"}</TableCell>
                    <TableCell>{item.tranDate || "-"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TextField
          label="Reason for closure (required)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          fullWidth
          required
          multiline
          minRows={3}
          helperText={`${Math.max(reasonRemaining, 0)} characters remaining`}
          error={trimmedReason.length > MAX_REASON_LENGTH}
        />

        {submitError && (
          <Typography color="error" sx={{ mt: 2 }}>
            {submitError}
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={() => navigate("/po-closures")}>
            Back to Audit Trail
          </Button>
          <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
            {submitLoading ? <CircularProgress size={22} /> : "Submit Closure"}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PoClosureForm;
